import React, { useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";
import RecipeIngredientSectionModal from "./RecipeIngredientSectionModal";
import {
    Recipe,
    RecipeCategory,
    RecipeProduct,
} from "../../../Recipe";
import RichTextEditor from "./RichTextEditor";

interface RecipeIngredientSection {
    id: number;
    recipe_id: number;
    component_name: string;
    ingredients: string;
    display_order: number;
}

interface RecipeModalProps {
    recipe: Recipe | null;
    categories: RecipeCategory[];
    products: RecipeProduct[];
    onClose: () => void;
    onSaved: () => void;
}

const RecipeModal = ({
                         recipe,
                         categories,
                         products,
                         onClose,
                         onSaved,
                     }: RecipeModalProps) => {
    const [categoryId, setCategoryId] = useState("");
    const [productId, setProductId] = useState("");

    const [name, setName] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [previewBody, setPreviewBody] = useState("");

    const [previewImage, setPreviewImage] =
        useState<File | null>(null);

    const [previewImageUrl, setPreviewImageUrl] =
        useState<string | null>(null);

    const [prepTime, setPrepTime] = useState("");
    const [cookTime, setCookTime] = useState("");
    const [overallTime, setOverallTime] = useState("");

    const [serves, setServes] = useState("");
    const [difficulty, setDifficulty] = useState("");

    const [method, setMethod] = useState("");

    const [active, setActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState(0);

    const [ingredientSections, setIngredientSections] =
        useState<RecipeIngredientSection[]>([]);

    const [
        ingredientSectionModalOpen,
        setIngredientSectionModalOpen,
    ] = useState(false);

    const [
        selectedIngredientSection,
        setSelectedIngredientSection,
    ] =
        useState<RecipeIngredientSection | null>(null);

    const [loading, setLoading] = useState(false);
    const [loadingIngredients, setLoadingIngredients] =
        useState(false);

    const [loadingIngredientAction, setLoadingIngredientAction] =
        useState<number | null>(null);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const isEditing = !!recipe;

    useEffect(() => {
        if (!recipe) {
            setCategoryId(
                categories.length > 0
                    ? String(categories[0].id)
                    : ""
            );

            setProductId("");
            setName("");
            setSubtitle("");
            setPreviewBody("");

            setPreviewImage(null);
            setPreviewImageUrl(null);

            setPrepTime("");
            setCookTime("");
            setOverallTime("");

            setServes("");
            setDifficulty("");
            setMethod("");

            setActive(true);
            setDisplayOrder(0);

            setIngredientSections([]);

            setError("");
            setSuccess("");

            return;
        }

        setCategoryId(
            recipe.recipe_category_id
                ? String(recipe.recipe_category_id)
                : ""
        );

        setProductId(
            recipe.product_id
                ? String(recipe.product_id)
                : ""
        );

        setName(recipe.name || "");
        setSubtitle(recipe.subtitle || "");
        setPreviewBody(recipe.preview_body || "");

        setPrepTime(recipe.prep_time || "");
        setCookTime(recipe.cook_time || "");
        setOverallTime(recipe.overall_time || "");

        setServes(
            recipe.serves !== null &&
            recipe.serves !== undefined
                ? String(recipe.serves)
                : ""
        );

        setDifficulty(recipe.difficulty || "");
        setMethod(recipe.method || "");

        setActive(recipe.active);
        setDisplayOrder(recipe.display_order ?? 0);

        setPreviewImage(null);

        if (recipe.preview_image) {
            setPreviewImageUrl(
                `${IMAGE_URL}/${recipe.preview_image}`
            );
        } else {
            setPreviewImageUrl(null);
        }

        setError("");
        setSuccess("");

        fetchIngredientSections(recipe.id);
    }, [recipe, categories]);

    const fetchIngredientSections = async (
        recipeId: number
    ) => {
        try {
            setLoadingIngredients(true);

            const response = await api.get(
                `/admin/recipe-ingredient-sections?recipe_id=${recipeId}`
            );

            const data =
                response.data?.data ??
                response.data ??
                [];

            setIngredientSections(
                [...data].sort(
                    (
                        a: RecipeIngredientSection,
                        b: RecipeIngredientSection
                    ) =>
                        a.display_order -
                        b.display_order
                )
            );
        } catch (err: any) {
            console.error(
                "Failed to load ingredient sections:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load ingredient sections."
            );
        } finally {
            setLoadingIngredients(false);
        }
    };

    const handleImageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        setPreviewImage(file);

        const objectUrl = URL.createObjectURL(file);

        setPreviewImageUrl(objectUrl);
    };

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!categoryId) {
            setError(
                "Please select a recipe category."
            );
            return;
        }

        if (!productId) {
            setError(
                "Please select a product."
            );
            return;
        }

        if (!name.trim()) {
            setError(
                "Please enter a recipe name."
            );
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();

            formData.append(
                "recipe_category_id",
                categoryId
            );

            formData.append(
                "product_id",
                productId
            );

            formData.append(
                "name",
                name.trim()
            );

            formData.append(
                "subtitle",
                subtitle.trim()
            );

            formData.append(
                "preview_body",
                previewBody.trim()
            );

            formData.append(
                "prep_time",
                prepTime.trim()
            );

            formData.append(
                "cook_time",
                cookTime.trim()
            );

            formData.append(
                "overall_time",
                overallTime.trim()
            );

            if (serves !== "") {
                formData.append(
                    "serves",
                    serves.trim()
                );
            }

            formData.append(
                "difficulty",
                difficulty.trim()
            );

            formData.append(
                "method",
                method
            );

            formData.append(
                "active",
                active ? "1" : "0"
            );

            formData.append(
                "display_order",
                String(
                    Number(displayOrder) || 0
                )
            );

            if (previewImage) {
                formData.append(
                    "preview_image",
                    previewImage
                );
            }

            if (isEditing) {
                formData.append(
                    "_method",
                    "PUT"
                );

                await api.post(
                    `/admin/recipes/${recipe.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                setSuccess(
                    "Recipe updated successfully."
                );
            } else {
                const response =
                    await api.post(
                        "/admin/recipes",
                        formData,
                        {
                            headers: {
                                "Content-Type":
                                    "multipart/form-data",
                            },
                        }
                    );

                /*
                 * The recipe must be saved first
                 * before ingredient sections can
                 * be attached to it.
                 */
                if (response.data?.data?.id) {
                    // The parent page will reload
                    // the recipe after saving.
                }

                setSuccess(
                    "Recipe created successfully."
                );
            }

            await onSaved();
        } catch (err: any) {
            console.error(
                "Failed to save recipe:",
                err
            );

            if (
                err.response?.data?.errors
            ) {
                const validationErrors =
                    Object.values(
                        err.response.data.errors
                    ).flat();

                setError(
                    validationErrors.length
                        ? String(
                            validationErrors[0]
                        )
                        : "Please check the form and try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save recipe."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddIngredientSection = () => {
        if (!recipe) {
            setError(
                "Save the recipe before adding ingredient sections."
            );
            return;
        }

        if (ingredientSections.length >= 4) {
            setError(
                "A recipe can have a maximum of 4 ingredient sections."
            );
            return;
        }

        setSelectedIngredientSection(null);
        setIngredientSectionModalOpen(true);
        setError("");
    };

    const handleEditIngredientSection = (
        section: RecipeIngredientSection
    ) => {
        setSelectedIngredientSection(section);
        setIngredientSectionModalOpen(true);
        setError("");
    };

    const handleCloseIngredientSectionModal = () => {
        setIngredientSectionModalOpen(false);
        setSelectedIngredientSection(null);
    };

    const handleIngredientSectionSaved = async () => {
        handleCloseIngredientSectionModal();

        if (recipe) {
            await fetchIngredientSections(
                recipe.id
            );
        }
    };

    const handleDeleteIngredientSection = async (
        section: RecipeIngredientSection
    ) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${section.component_name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setLoadingIngredientAction(
                section.id
            );
            setError("");

            await api.delete(
                `/admin/recipe-ingredient-sections/${section.id}`
            );

            if (recipe) {
                await fetchIngredientSections(
                    recipe.id
                );
            }
        } catch (err: any) {
            console.error(
                "Failed to delete ingredient section:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to delete ingredient section."
            );
        } finally {
            setLoadingIngredientAction(
                null
            );
        }
    };

    const handleMoveIngredientSection = async (
        section: RecipeIngredientSection,
        direction: "up" | "down"
    ) => {
        try {
            setLoadingIngredientAction(
                section.id
            );
            setError("");

            await api.post(
                `/admin/recipe-ingredient-sections/${section.id}/move`,
                {
                    direction,
                }
            );

            if (recipe) {
                await fetchIngredientSections(
                    recipe.id
                );
            }
        } catch (err: any) {
            console.error(
                "Failed to move ingredient section:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to change ingredient section order."
            );
        } finally {
            setLoadingIngredientAction(
                null
            );
        }
    };

    const canMoveIngredientSection = (
        index: number,
        direction: "up" | "down"
    ) => {
        if (direction === "up") {
            return index > 0;
        }

        return (
            index <
            ingredientSections.length - 1
        );
    };

    return (
        <>
            <div
                className="admin-modal-overlay"
                onClick={onClose}
            >
                <div
                    className="admin-modal admin-recipe-modal"
                    onClick={(event) =>
                        event.stopPropagation()
                    }
                >
                    <div className="admin-modal-header">
                        <h2>
                            {isEditing
                                ? "Edit Recipe"
                                : "Add Recipe"}
                        </h2>

                        <button
                            type="button"
                            className="admin-modal-close"
                            onClick={onClose}
                            disabled={loading}
                        >
                            ×
                        </button>
                    </div>

                    <div className="admin-modal-content">
                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {/* CATEGORY */}

                        <div className="form-group">
                            <label htmlFor="recipe-category">
                                Category
                            </label>

                            <select
                                id="recipe-category"
                                value={categoryId}
                                onChange={(event) =>
                                    setCategoryId(
                                        event.target.value
                                    )
                                }
                                disabled={loading}
                            >
                                <option value="">
                                    Select category
                                </option>

                                {categories.map(
                                    (category) => (
                                        <option
                                            key={
                                                category.id
                                            }
                                            value={
                                                category.id
                                            }
                                        >
                                            {
                                                category.name
                                            }
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        {/* PRODUCT */}

                        <div className="form-group">
                            <label htmlFor="recipe-product">
                                Product
                            </label>

                            <select
                                id="recipe-product"
                                value={productId}
                                onChange={(event) =>
                                    setProductId(
                                        event.target.value
                                    )
                                }
                                disabled={loading}
                            >
                                <option value="">
                                    Select product
                                </option>

                                {products.map(
                                    (product) => (
                                        <option
                                            key={
                                                product.id
                                            }
                                            value={
                                                product.id
                                            }
                                        >
                                            {
                                                product.name
                                            }
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        {/* NAME */}

                        <div className="form-group">
                            <label htmlFor="recipe-name">
                                Recipe Name
                            </label>

                            <input
                                id="recipe-name"
                                type="text"
                                value={name}
                                onChange={(event) =>
                                    setName(
                                        event.target.value
                                    )
                                }
                                placeholder="Recipe name"
                                disabled={loading}
                            />
                        </div>

                        {/* PREVIEW IMAGE */}

                        <div className="form-group">
                            <label htmlFor="recipe-preview-image">
                                Preview Image
                            </label>

                            <input
                                id="recipe-preview-image"
                                type="file"
                                accept="image/*"
                                onChange={
                                    handleImageChange
                                }
                                disabled={loading}
                            />

                            {previewImageUrl && (
                                <div className="admin-image-preview">
                                    <img
                                        src={
                                            previewImageUrl
                                        }
                                        alt="Recipe preview"
                                    />
                                </div>
                            )}
                        </div>

                        {/* SUBTITLE */}

                        <div className="form-group">
                            <label htmlFor="recipe-subtitle">
                                Subtitle
                            </label>

                            <textarea
                                id="recipe-subtitle"
                                value={subtitle}
                                onChange={(event) =>
                                    setSubtitle(
                                        event.target.value
                                    )
                                }
                                rows={3}
                                placeholder="Recipe subtitle"
                                disabled={loading}
                            />
                        </div>

                        {/* PREVIEW BODY */}

                        <div className="form-group">
                            <label htmlFor="recipe-preview-body">
                                Preview Body
                            </label>

                            <textarea
                                id="recipe-preview-body"
                                value={previewBody}
                                onChange={(event) =>
                                    setPreviewBody(
                                        event.target.value
                                    )
                                }
                                rows={5}
                                placeholder="Recipe preview text"
                                disabled={loading}
                            />
                        </div>

                        {/* TIMES */}

                        <div className="recipe-form-grid">
                            <div className="form-group">
                                <label htmlFor="recipe-prep-time">
                                    Prep Time
                                </label>

                                <input
                                    id="recipe-prep-time"
                                    type="text"
                                    value={prepTime}
                                    onChange={(event) =>
                                        setPrepTime(
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. 15 mins"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="recipe-cook-time">
                                    Cook Time
                                </label>

                                <input
                                    id="recipe-cook-time"
                                    type="text"
                                    value={cookTime}
                                    onChange={(event) =>
                                        setCookTime(
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. 30 mins"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="recipe-overall-time">
                                    Overall Time
                                </label>

                                <input
                                    id="recipe-overall-time"
                                    type="text"
                                    value={overallTime}
                                    onChange={(event) =>
                                        setOverallTime(
                                            event.target.value
                                        )
                                    }
                                    placeholder="e.g. 45 mins"
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        {/* SERVES */}

                        <div className="form-group">
                            <label htmlFor="recipe-serves">
                                Serves
                            </label>

                            <input
                                id="recipe-serves"
                                type="text"
                                value={serves}
                                onChange={(event) =>
                                    setServes(
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Serves 4"
                                disabled={loading}
                            />
                        </div>

                        {/* DIFFICULTY */}

                        <div className="form-group">
                            <label htmlFor="recipe-difficulty">
                                Difficulty
                            </label>

                            <input
                                id="recipe-difficulty"
                                type="text"
                                value={difficulty}
                                onChange={(event) =>
                                    setDifficulty(
                                        event.target.value
                                    )
                                }
                                placeholder="e.g. Easy"
                                disabled={loading}
                            />
                        </div>

                        {/* METHOD */}

                        <div className="form-group">
                            <label>
                                Method
                            </label>

                            <RichTextEditor
                                value={method}
                                onChange={setMethod}
                                placeholder="Enter the recipe method..."
                            />
                        </div>

                        {/* INGREDIENT SECTIONS */}

                        {isEditing && (
                            <div className="form-group">
                                <div className="admin-recipe-ingredients-header">
                                    <div>
                                        <label>
                                            Ingredient Sections
                                        </label>

                                        <p>
                                            Add up to 4
                                            ingredient
                                            sections to
                                            this recipe.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        className="admin-primary-button"
                                        onClick={
                                            handleAddIngredientSection
                                        }
                                        disabled={
                                            loading ||
                                            loadingIngredients ||
                                            ingredientSections.length >=
                                            4
                                        }
                                    >
                                        Add ingredient section
                                    </button>
                                </div>

                                {loadingIngredients ? (
                                    <p>
                                        Loading ingredient
                                        sections...
                                    </p>
                                ) : ingredientSections.length ===
                                0 ? (
                                    <p>
                                        No ingredient
                                        sections added
                                        yet.
                                    </p>
                                ) : (
                                    <div className="admin-recipe-ingredients-list">
                                        {ingredientSections.map(
                                            (
                                                section,
                                                index
                                            ) => (
                                                <div
                                                    key={
                                                        section.id
                                                    }
                                                    className="admin-recipe-ingredient-item"
                                                >
                                                    <div className="admin-recipe-ingredient-info">
                                                        <strong>
                                                            {
                                                                section.component_name
                                                            }
                                                        </strong>

                                                        <div
                                                            className="admin-recipe-ingredient-preview"
                                                            dangerouslySetInnerHTML={{
                                                                __html:
                                                                section.ingredients,
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="admin-user-actions">
                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleMoveIngredientSection(
                                                                    section,
                                                                    "up"
                                                                )
                                                            }
                                                            disabled={
                                                                !canMoveIngredientSection(
                                                                    index,
                                                                    "up"
                                                                ) ||
                                                                loadingIngredientAction !==
                                                                null
                                                            }
                                                            aria-label={`Move ${section.component_name} up`}
                                                        >
                                                            ↑
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleMoveIngredientSection(
                                                                    section,
                                                                    "down"
                                                                )
                                                            }
                                                            disabled={
                                                                !canMoveIngredientSection(
                                                                    index,
                                                                    "down"
                                                                ) ||
                                                                loadingIngredientAction !==
                                                                null
                                                            }
                                                            aria-label={`Move ${section.component_name} down`}
                                                        >
                                                            ↓
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-table-button"
                                                            onClick={() =>
                                                                handleEditIngredientSection(
                                                                    section
                                                                )
                                                            }
                                                            disabled={
                                                                loadingIngredientAction !==
                                                                null
                                                            }
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="admin-table-button admin-table-button-danger"
                                                            onClick={() =>
                                                                handleDeleteIngredientSection(
                                                                    section
                                                                )
                                                            }
                                                            disabled={
                                                                loadingIngredientAction ===
                                                                section.id
                                                            }
                                                        >
                                                            {loadingIngredientAction ===
                                                            section.id
                                                                ? "..."
                                                                : "Delete"}
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}

                                {ingredientSections.length >=
                                    4 && (
                                        <p className="form-help">
                                            Maximum of 4 ingredient
                                            sections reached.
                                        </p>
                                    )}
                            </div>
                        )}

                        {/* DISPLAY ORDER */}

                        <div className="form-group">
                            <label htmlFor="recipe-display-order">
                                Display Order
                            </label>

                            <input
                                id="recipe-display-order"
                                type="number"
                                min="0"
                                value={displayOrder}
                                onChange={(event) =>
                                    setDisplayOrder(
                                        Number(
                                            event.target.value
                                        )
                                    )
                                }
                                disabled={loading}
                            />
                        </div>

                        {/* ACTIVE */}

                        <div className="form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(event) =>
                                        setActive(
                                            event.target.checked
                                        )
                                    }
                                    disabled={loading}
                                />

                                <span>
                                    Active
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="admin-primary-button"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : isEditing
                                    ? "Save Changes"
                                    : "Add Recipe"}
                        </button>
                    </div>
                </div>
            </div>

            {/* INGREDIENT SECTION MODAL */}

            {ingredientSectionModalOpen &&
                recipe && (
                    <RecipeIngredientSectionModal
                        recipe={recipe}
                        section={
                            selectedIngredientSection
                        }
                        onClose={
                            handleCloseIngredientSectionModal
                        }
                        onSaved={
                            handleIngredientSectionSaved
                        }
                    />
                )}
        </>
    );
};

export default RecipeModal;
